import { Button, Checkbox, Modal, SearchInput } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../../client/gql";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { Avatar } from "../../Avatar/Avatar";
import { MemberRow } from "../OmniOrgView/MemberRow";
import css from "../orgList.module.scss";

// Colocated fragment: the profile fields this picker renders.
const GroupMembersModal_Profile = graphql(`
  fragment GroupMembersModal_Profile on Profile {
    uuid
    firstName
    lastName
    email
  }
`);

const GroupMembersModalQuery = graphql(`
  query GroupMembersModal_Query($where: ProfileWhere) {
    profiles(where: $where, options: { sort: { lastName: ASC } }) {
      ...GroupMembersModal_Profile
    }
  }
`);

const GroupMembersModalUpdateGroup = graphql(`
  mutation GroupMembersModal_UpdateGroup($where: GroupWhere, $update: GroupUpdateInput) {
    updateGroups(where: $where, update: $update) {
      groups {
        uuid
      }
    }
  }
`);

type Props = {
  org: { uuid: string; name: string };
  group: { uuid: string; name: string; memberUuids: readonly string[] };
  onClose: () => void;
  onSaved: () => void;
};

export function GroupMembersModal({ org, group, onClose, onSaved }: Props) {
  const [{ data }] = useQuery({
    query: GroupMembersModalQuery,
    variables: { where: { organization: { uuid: org.uuid } } },
  });
  const [, updateGroup] = useMutation(GroupMembersModalUpdateGroup);

  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set(group.memberUuids));

  const profiles = getFragmentData(GroupMembersModal_Profile, data?.profiles ?? []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      return profiles;
    }
    return profiles.filter((p) => {
      const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.toLowerCase();
      return name.includes(s) || (p.email ?? "").toLowerCase().includes(s);
    });
  }, [profiles, q]);

  const toggle = (uuid: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });

  const save = async () => {
    const initial = new Set(group.memberUuids);
    const toConnect = [...selected].filter((u) => !initial.has(u));
    const toDisconnect = [...initial].filter((u) => !selected.has(u));
    if (toConnect.length === 0 && toDisconnect.length === 0) {
      onClose();
      return;
    }
    const profilesUpdate: {
      connect?: { where: { node: { uuid: string } } }[];
      disconnect?: { where: { node: { uuid: string } } }[];
    } = {};
    if (toConnect.length > 0) {
      profilesUpdate.connect = toConnect.map((uuid) => ({ where: { node: { uuid } } }));
    }
    if (toDisconnect.length > 0) {
      profilesUpdate.disconnect = toDisconnect.map((uuid) => ({ where: { node: { uuid } } }));
    }
    const res = await updateGroup({
      where: { uuid: group.uuid },
      update: { profiles: [profilesUpdate] },
    });
    if (res.error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast(`Mitglieder von „${group.name}" aktualisiert`);
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title={`Mitglieder — ${group.name}`}
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>
            {selected.size} von {profiles.length} Mitgliedern zugeordnet
          </span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" onClick={() => void save()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <SearchInput
          name="group-member-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name oder E-Mail …"
        />
        <div className={css.modalScroll}>
          {filtered.map((p) => {
            const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim() || p.email || "—";
            const on = selected.has(p.uuid);
            return (
              <MemberRow
                key={p.uuid}
                selected={on}
                onClick={() => toggle(p.uuid)}
                checkbox={
                  <Checkbox
                    name={`group-sel-${p.uuid}`}
                    checked={on}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggle(p.uuid)}
                  />
                }
                status={<Avatar name={name} size={30} />}
                statusTint="transparent"
                name={name}
                sub={p.email}
              />
            );
          })}
          {filtered.length === 0 && (
            <div style={{ padding: "14px 16px", color: "var(--color-grey)", fontSize: 13 }}>
              Keine Mitglieder gefunden.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
